<script lang="ts">
	import { applicationFormId } from "@components/applicationFormId.js";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { getTranslate } from "@utils/translate.js";
	import { onMount } from "svelte";
	import type { ClassValue } from "svelte/elements";
	import eventIcon from "@core/assets/icons/event.svg?raw";
	import locationIcon from "@core/assets/icons/location.svg?raw";
	import type { Competition } from "@core/schema/Competition.js";
	import { getApplicationPath } from "@core/schema/paths/getApplicationPath.js";
	import { getApplicationsPath } from "@core/schema/paths/getApplicationsPath.js";
	import { toDateMonthFormat } from "@core/utils/toDateMonthFormat.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./Banner.translations.json" with { type: "json" };
	import Countdown from "./Countdown.svelte";
	import Icon from "./common/Icon.svelte";

	const { class: customClass, competition }: { class?: ClassValue; competition: Competition } = $props();
	const translate = getTranslate(translations);
	const { tenant } = getGlobalTemplateProps();
	let now = $state(new Date().toISOString());
	onMount(() => {
		const interval = setInterval(() => {
			now = new Date().toISOString();
		}, 1000);
		return () => {
			clearInterval(interval);
		};
	});
</script>

<section class={["mb-8 lg:mb-14 z-20", customClass]}>
	<div
		class="bg-ink-950 relative overflow-hidden rounded-[20px] px-5 py-10 text-white shadow-elevated sm:rounded-[22px] sm:px-10 sm:py-14 lg:px-14"
	>
		<!-- blue → ink wash -->
		<div
			class="pointer-events-none absolute inset-0"
			style="background: linear-gradient(135deg, rgba(56,113,193,0.36) 0%, rgba(56,113,193,0.07) 38%, rgba(32,31,31,0) 66%);"
			aria-hidden="true"
		></div>
		<!-- disc motif -->
		<svg
			class="pointer-events-none absolute -right-16 -top-24 h-[340px] w-[340px] text-white/[0.05]"
			viewBox="0 0 100 100"
			fill="none"
			stroke="currentColor"
			stroke-width="1.2"
			aria-hidden="true"
		>
			<circle cx="50" cy="50" r="48" /><circle cx="50" cy="50" r="33" /><circle cx="50" cy="50" r="6" />
		</svg>

		<div class="relative grid items-center gap-8 md:grid-cols-[1.4fr_1fr] lg:gap-14">
			<div class="ff-fade-up flex flex-col gap-5 max-md:order-2">
				<p class="eyebrow text-sand-300">{translate("operatorEyebrow")}</p>
				<h1 class="!mb-0 max-w-2xl !text-white !leading-[0.95] !text-[clamp(1.9rem,5.5vw,3.5rem)]">
					{competition.headline[tenant.locale]}
				</h1>

				<div class="flex flex-col gap-2 text-base sm:flex-row sm:flex-wrap sm:gap-3">
					<span
						class="flex w-fit items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-4 py-2 font-semibold leading-normal backdrop-blur-sm"
					>
						<Icon icon={locationIcon} class="text-sand-300 h-5 w-5" />
						{competition.location[tenant.locale]}
					</span>
					<span
						class="flex w-fit items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-4 py-2 font-semibold leading-normal backdrop-blur-sm"
					>
						<Icon icon={eventIcon} class="text-sand-300 h-5 w-5" />
						{toDateMonthFormat(competition.competitionStart, tenant.locale)} – {toDateMonthYearFormat(
							competition.competitionEnd,
							tenant.locale,
						)}
					</span>
				</div>

				{#if competition.registrationStart > now}
					<div class="flex flex-col gap-2">
						<p class="eyebrow text-sand-300">{translate("toStartOfRegistration")}</p>
						<Countdown target={competition.registrationStart} />
					</div>
				{:else if competition.registrationEnd > now}
					<div class="flex flex-col gap-2">
						<p class="eyebrow text-sand-300">{translate("toEndOfRegistration")}</p>
						<Countdown target={competition.registrationEnd} />
					</div>
				{:else if competition.competitionStart > now}
					<div class="flex flex-col gap-2">
						<p class="eyebrow text-sand-300">{translate("toStartOfCompetition")}</p>
						<Countdown target={competition.competitionStart} />
					</div>
				{/if}

				<div class="mt-2 flex flex-col gap-3 sm:flex-row">
					{#if now >= competition.registrationStart}
						{#if now < competition.registrationEnd}
							<Link
								class="max-md:w-full"
								variant={ButtonVariant.Primary}
								href="{getApplicationPath(competition, tenant.locale)}#{applicationFormId[tenant.locale]}"
							>
								{translate("registrationButton")}
							</Link>
						{/if}
						<Link
							class="ease-spring inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-md border-2 border-white/25 px-6 py-3 font-semibold tracking-[0.02em] text-white transition duration-150 hover:-translate-y-px hover:border-white/50 hover:bg-white/10 active:scale-[0.97] max-md:w-full md:px-8"
							href={getApplicationsPath(competition, tenant.locale)}
						>
							{translate("listOfApplications")}
						</Link>
					{/if}
				</div>
			</div>

			<div class="max-md:order-1">
				{#if competition.coverImage}
					<div
						class="aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 shadow-elevated ring-1 ring-white/10"
					>
						<Media
							class="h-full w-full object-cover"
							wrapperClass="block h-full w-full"
							source={competition.coverImage}
							width={828}
							height={554}
							alt={competition.headline[tenant.locale]}
						/>
					</div>
				{:else}
					<div class="flex items-center justify-center py-4">
						<svg
							class="h-52 w-52 text-white/[0.07]"
							viewBox="0 0 100 100"
							fill="none"
							stroke="currentColor"
							stroke-width="1.2"
							aria-hidden="true"
						>
							<circle cx="50" cy="50" r="48" /><circle cx="50" cy="50" r="33" /><circle cx="50" cy="50" r="6" />
						</svg>
					</div>
				{/if}
			</div>
		</div>
	</div>
</section>
